'use client';

import React, { useState } from 'react';

import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { WhatsAppInlineButton } from '@/components/WhatsAppButton';
import SuccessModal from '@/app/components/SuccessModal';
import { trackContactFormSubmit, trackPhoneCall } from '@/lib/conversions';
import SiteInternalLinks from '@/components/SiteInternalLinks';
import MultiSelectDienst from '@/app/components/MultiSelectDienst';
import PageHero from '@/components/responsive/PageHero';

export default function ContactPage() {
  const [formData, setFormData] = useState({ naam: '', telefoon: '', email: '', diensten: [] as string[], bericht: '' });
  const [sending, setSending] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [sendError, setSendError] = useState('');
  const [emailError, setEmailError] = useState('');

  const update = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === 'email') setEmailError('');
  };

  const validateEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.email) {
      setEmailError('E-mailadres is verplicht.');
      return;
    }
    if (!validateEmail(formData.email)) {
      setEmailError('Voer een geldig e-mailadres in.');
      return;
    }
    setSending(true);
    setSendError('');
    try {
      const res = await fetch('/api/send-contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          dienst: formData.diensten.join(', '),
          diensten: formData.diensten,
          bron: 'Contactpagina',
        }),
      });
      if (!res.ok) {
        setSendError('Er is een fout opgetreden. Probeer opnieuw of bel ons.');
      } else {
        trackContactFormSubmit('contact-page');
        setShowModal(true);
      }
    } catch {
      setSendError('Er is een fout opgetreden. Probeer opnieuw of bel ons.');
    } finally {
      setSending(false);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setFormData({ naam: '', telefoon: '', email: '', diensten: [], bericht: '' });
  };

  return (
    <>
      <SuccessModal visible={showModal} naam={formData.naam} onClose={handleModalClose} />
      <Header />
      <main>
        <PageHero
          breadcrumbs={[
            { label: 'Home', href: '/' },
            { label: 'Contact' },
          ]}
          title="Contact Roetfilter Center"
          description="Heeft u een vraag over roetfilter reiniging, revisie of vervanging? Of wilt u direct een afspraak maken? Ons team van specialisten helpt u graag verder."
          whatsappMessage="Hallo, ik wil graag contact opnemen met Roetfilter Center."
        />

        <section className="rc-section bg-white">
          <div className="rc-container">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <h2 className="rc-h2 mb-6">Contactgegevens</h2>
                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-4 p-4 rc-card-soft rounded-2xl">
                    <span className="text-2xl">📞</span>
                    <div>
                      <p className="font-extrabold text-foreground">Telefoon</p>
                      <a href="tel:0498545474" onClick={() => { if (typeof (window as any).gtag_report_conversion === 'function') (window as any).gtag_report_conversion('tel:0498545474'); }} className="text-primary font-bold text-lg hover:underline">0498 54 54 74</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-4 rc-card-soft rounded-2xl">
                    <span className="text-2xl">✉️</span>
                    <div>
                      <p className="font-extrabold text-foreground">E-mail</p>
                      <a href="mailto:info@roetfilter-center.be" className="text-primary font-bold hover:underline">info@roetfilter-center.be</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-4 rc-card-soft rounded-2xl">
                    <span className="text-2xl">📍</span>
                    <div>
                      <p className="font-extrabold text-foreground">Adres</p>
                      <p className="text-muted-foreground">Dijkstraat 22 – 9160 Lokeren</p>
                      <a href="https://www.google.com/maps?q=Dijkstraat+22,+9160+Lokeren" target="_blank" rel="noopener noreferrer" className="text-primary text-sm font-semibold hover:underline">Bekijk op Google Maps →</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-4 rc-card-soft rounded-2xl">
                    <span className="text-2xl">⏰</span>
                    <div>
                      <p className="font-extrabold text-foreground">Openingstijden</p>
                      <p className="text-muted-foreground text-sm">Maandag – Vrijdag: 08:00 – 18:30</p>
                      <p className="text-muted-foreground text-sm">Zaterdag: 09:00 – 17:30</p>
                      <p className="text-muted-foreground text-sm">Zondag: op afspraak</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-4 rc-card-soft rounded-2xl">
                    <span className="text-2xl">💬</span>
                    <div>
                      <p className="font-extrabold text-foreground">WhatsApp</p>
                      <a href="https://wa.me/32498545474" target="_blank" rel="noopener noreferrer" className="text-primary font-bold hover:underline">0498 54 54 74</a>
                    </div>
                  </div>
                </div>

                <h3 className="text-xl font-extrabold text-foreground mb-4">Onze Diensten in een Oogopslag</h3>
                <ul className="space-y-2">
                  {['Roetfilter reinigen (auto & bestelwagen)', 'Roetfilter revisie & vervanging', 'Diagnose & foutcode-uitlezing', 'Originele of OEM-onderdelen', 'Klaar binnen 1 werkdag'].map((s) => (
                    <li key={s} className="flex items-start gap-2 text-sm text-foreground"><span className="text-primary font-bold">✓</span> {s}</li>
                  ))}
                </ul>
              </div>

              <div>
                <h2 className="text-3xl font-extrabold text-foreground mb-6">Stuur ons een Bericht</h2>
                <div className="rc-card-soft p-6 lg:p-7">
                  {showModal ? (
                    <div className="text-center py-10 space-y-4">
                      <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
                        <svg className="w-8 h-8 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <h4 className="text-xl font-extrabold text-foreground">Bericht verzonden!</h4>
                      <p className="text-sm text-muted-foreground">Wij nemen contact op — u hoort van ons binnen 24 uur.</p>
                      <button onClick={handleModalClose} className="text-sm text-primary hover:underline">
                        Nieuw bericht sturen
                      </button>
                    </div>
                  ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-bold text-foreground mb-2">Naam *</label>
                      <input
                        type="text"
                        required
                        value={formData.naam}
                        onChange={(e) => update('naam', e.target.value)}
                        className="w-full border border-border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                        placeholder="Uw naam"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-foreground mb-2">Telefoon *</label>
                      <input
                        type="tel"
                        required
                        value={formData.telefoon}
                        onChange={(e) => update('telefoon', e.target.value)}
                        className="w-full border border-border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                        placeholder="Uw telefoonnummer"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-foreground mb-2">E-mail *</label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => update('email', e.target.value)}
                        className="w-full border border-border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                        placeholder="Uw e-mailadres"
                      />
                      {emailError && <p className="text-red-600 text-sm mt-1">{emailError}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-foreground mb-2">Probleem / Dienst</label>
                      <MultiSelectDienst
                        value={formData.diensten}
                        onChange={(selected) => setFormData((prev) => ({ ...prev, diensten: selected }))}
                        inputClassName="py-3 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-foreground mb-2">Bericht</label>
                      <textarea
                        rows={4}
                        value={formData.bericht}
                        onChange={(e) => update('bericht', e.target.value)}
                        className="w-full border border-border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-white resize-none"
                        placeholder="Beschrijf uw probleem of vraag"
                      />
                    </div>
                    {sendError && (
                      <p className="text-sm text-red-600">{sendError}</p>
                    )}
                    <button
                      type="submit"
                      disabled={sending}
                      className="w-full bg-primary text-white py-4 rounded-full font-extrabold text-base hover:opacity-90 transition-opacity disabled:opacity-60"
                    >
                      {sending ? 'Verzenden...' : '✉️ Verstuur Bericht'}
                    </button>
                    <div className="flex items-center gap-3 my-2">
                      <div className="flex-1 h-px bg-border" />
                      <span className="text-xs text-muted-foreground font-medium">of</span>
                      <div className="flex-1 h-px bg-border" />
                    </div>
                    <a
                      href="tel:0498545474"
                      onClick={() => { trackPhoneCall('contact-page'); if (typeof (window as any).gtag_report_conversion === 'function') (window as any).gtag_report_conversion('tel:0498545474'); }}
                      className="w-full flex items-center justify-center gap-2 border-2 border-primary text-primary py-3.5 rounded-full font-bold text-sm hover:bg-primary/5 transition-colors"
                    >
                      📞 Bel Direct: 0498 54 54 74
                    </a>
                    <WhatsAppInlineButton
                      label="Stuur een bericht"
                      message="Hallo, ik heb een vraag voor Roetfilter Center."
                      className="w-full justify-center py-4 text-base"
                      location="contact-page"
                    />
                  </form>
                  )}
                </div>

                <div className="mt-6 rc-card p-6 lg:p-7 border-primary/20 bg-primary/5">
                  <h3 className="font-extrabold text-foreground mb-3">Waarom Contact Opnemen?</h3>
                  <ul className="space-y-2">
                    {['Snelle reactie en persoonlijke service', 'Ervaren technici met kennis van alle automerken', 'Duidelijke uitleg en transparante prijzen', 'Garantie op montage en reiniging', 'Service voor zowel particulieren als bedrijven'].map((s) => (
                      <li key={s} className="flex items-start gap-2 text-sm text-foreground"><span className="text-primary font-bold">✓</span> {s}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        <SiteInternalLinks variant="contact" />
      </main>
      <Footer />
    </>
  );
}
